import { useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Eye, EyeOff } from 'lucide-react';
import { getCategoryColor, getCategoryName } from '../../utils/sceneUtils';
import {
    describeOpeningSpacing,
    formatArea,
    formatDimensionTriplet,
    formatLinearMeasurement,
    formatWallEdges,
    getUnitSystemSuffix,
    inferRectanglePolygon,
    normalizeUnitSystem,
    UNIT_SYSTEMS,
} from '../../utils/measurementUtils';

// Section header labels. Exported so tests assert against the same source
// of truth instead of duplicating literals (a renamed label can't silently
// drift past the tests).
export const SECTION_LABELS = Object.freeze({
    object: 'Objects',
    wall: 'Walls',
    door: 'Doors',
    window: 'Windows',
    floor: 'Floors',
    opening: 'Openings',
    // ``Unknown`` is the diagnostic bucket the backend fills when iOS
    // sends a top-level scan key the ingest doesn't recognize. Its
    // section only renders when the unknown array is non-empty, so the
    // sidebar stays quiet for normal scans.
    unknown: 'Unknown',
});

// Stable empty-set default for `hiddenIds` so the prop default doesn't
// create a fresh reference on every render (which would invalidate the
// `sections`/`allItems` memos downstream).
const EMPTY_HIDDEN = new Set();

/**
 * Tri-state visibility for a section, derived from its members' ids and the
 * set of currently-hidden ids. Pure + exported so the header eye-icon logic
 * is unit-testable without rendering.
 *   'all'  → every member visible  → header shows Eye,    click hides all
 *   'none' → every member hidden   → header shows EyeOff, click shows all
 *   'some' → mixed                 → header shows Eye,    click hides all
 *
 * @param {string[]} ids - The scene identifiers governed by this section.
 * @param {Set<string>} hiddenIds - Identifiers currently hidden.
 * @returns {'all'|'none'|'some'}
 */
export const getSectionVisibility = (ids, hiddenIds) => {
    if (!ids || ids.length === 0) return 'all';
    const hidden = ids.filter(id => hiddenIds.has(id)).length;
    if (hidden === 0) return 'all';
    if (hidden === ids.length) return 'none';
    return 'some';
};

/**
 * Returns Tailwind CSS classes for the type badge based on item type.
 * Each type uses a distinct semantic token from the Hestia
 * palette so the badges remain distinguishable in both light and dark mode.
 */
const getTypeBadgeClasses = (type) => {
    switch (type) {
        case 'object': return 'bg-[var(--secondary-container)] text-[var(--secondary-container-text)]';
        case 'wall': return 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]';
        case 'door': return 'bg-[var(--tertiary-fixed)] text-[var(--text-main)]';
        // --primary-container / --primary-container-text is the contrast-safe
        // primary pair. --primary-bg is semi-transparent in dark mode and was
        // failing WCAG AA against --primary text.
        case 'window': return 'bg-[var(--primary-container)] text-[var(--primary-container-text)]';
        // Floors share the tertiary container pair with doors but with a
        // muted-text tone — they're structural like walls but worth
        // distinguishing from openings at a glance.
        case 'floor': return 'bg-[var(--tertiary-container)] text-[var(--tertiary-container-text)]';
        // Openings are holes in walls — pair them visually with doors.
        case 'opening': return 'bg-[var(--tertiary-fixed)] text-[var(--text-secondary)]';
        // Unknown items use the amber --unknown palette to flag "this
        // came from iOS but the ingest doesn't understand it yet" —
        // visually alerting without competing with the red selection
        // state or being misread as a hard error.
        case 'unknown': return 'bg-[var(--unknown-container)] text-[var(--unknown)]';
        default: return 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]';
    }
};

/**
 * Displays a scrollable list of all objects in the 3D scene with selection support.
 * Automatically scrolls selected items into view and synchronizes with 3D viewer selection.
 * Supports keyboard navigation (Enter/Space to select, arrow keys to navigate).
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.objects - Array of scene objects with identifier, category, dimensions
 * @param {Object[]} props.walls - Array of wall objects
 * @param {Object[]} props.doors - Array of door objects
 * @param {Object[]} props.windows - Array of window objects
 * @param {Object[]} props.floors - Array of floor surfaces
 * @param {number|null} props.floorArea - Backend-computed floor area in m² (display only; ADR 0006)
 * @param {string|null} props.floorAreaSource - Provenance: 'polygon_computed' | 'extracted' | 'user_override'
 * @param {number[]|null} props.floorFootprintM - True polygon footprint [width, depth] in meters
 * @param {Object[]} props.floorSections - Per-section floor breakdown (area + footprint per scanned room)
 * @param {Object[]} props.openings - Array of opening surfaces (doorless passages)
 * @param {Object[]} props.unknown - Diagnostic bucket: [{key: string, items: any[]}] for keys the backend ingest didn't recognize
 * @param {string|null} props.selectedId - Currently selected object ID, or null if none
 * @param {Function} props.onSelect - Callback when item is selected, receives object ID
 * @param {Set<string>} props.hiddenIds - Set of scene identifiers currently hidden from the 3D view
 * @param {Function} props.onToggleItem - Callback to toggle one object's visibility, receives object ID
 * @param {Function} props.onToggleSection - Callback to toggle a group's visibility, receives (ids[], makeVisible)
 * @param {'meters'|'feet'|'inches'} props.unitSystem - Unit system for displayed dimensions
 * @param {string} props.className - Additional CSS classes
 */
const SceneObjectList = ({
    objects = [],
    walls = [],
    doors = [],
    windows = [],
    floors = [],
    floorArea = null,
    // Provenance is part of the prop contract and will drive a future "edited"
    // badge when user-editable dimensions ship (ADR 0006). Accepted now so the
    // hook exists; intentionally not yet rendered.
    // eslint-disable-next-line no-unused-vars
    floorAreaSource = null,
    floorFootprintM = null,
    floorSections = [],
    openings = [],
    unknown = [],
    // Derived wall measurements keyed by wall UUID (net/paintable area + a
    // per-opening breakdown: gap to the previous opening, distance to the
    // nearest wall corner, sill). Computed server-side (services/
    // wall_geometry.py) and only formatted here. Absent on legacy responses,
    // hence the `{}` default.
    wallMeasurements = {},
    selectedId = null,
    onSelect,
    hiddenIds = EMPTY_HIDDEN,
    onToggleItem,
    onToggleSection,
    unitSystem = UNIT_SYSTEMS.INCHES,
    className = ''
}) => {
    const listRef = useRef(null);
    const itemRefs = useRef({});

    // Build the list as ordered SECTIONS in the following order:
    //   Walls → Floors → Openings → Doors → Windows → Objects → Unknown.
    // Walls + Floors come first because they're structural. Openings sit
    // with Doors because they're functionally the same (a hole in a
    // wall). Unknown is last because it's diagnostic — surfaced only
    // when the ingest sees a key it doesn't recognize.
    //
    // Each section's header doubles as its stat (label + item count).
    // Empty sections are omitted entirely (a room with no doors shows
    // no Doors header). Walls are always listed — their visibility in the
    // 3D scene is now controlled per-row / per-section via the eye toggle,
    // not by omitting them from the list.
    const sections = useMemo(() => {
        const result = [];

        const wallItems = walls.map(wall => ({
            id: wall.identifier,
            type: 'wall',
            category: 'wall',
            dimensions: wall.dimensions,
            // `polygonCorners` is canonical here — the upstream
            // FloorPlanViewer normalizes the snake_case backend
            // shape and the camelCase RoomPlan shape into this
            // single field via `normalizePolygonCasing`.
            polygonCorners: wall.polygonCorners,
            color: '#9ca3af',
        }));
        if (wallItems.length > 0) {
            result.push({ type: 'wall', label: SECTION_LABELS.wall, items: wallItems });
        }

        // Floor rows show the backend-computed area + true polygon footprint
        // (served in m²; ADR 0006), NOT RoomPlan's padded `dimensions` bbox —
        // the same true-geometry convention walls use. Area tracks the unit
        // SYSTEM (metric → m², imperial → ft²) via the area-specific suffix,
        // while the footprint uses the chosen linear unit — so a row can read
        // "123.0 ft² · Width 209in · Depth 95in" and be internally correct.
        const unit = normalizeUnitSystem(unitSystem);
        const linearSuffix = getUnitSystemSuffix(unit);

        // Build the "<area> · Width <w> · Depth <d>" string for one floor,
        // given a served area (m²) and optional polygon footprint [w, d] (m).
        // Returns null when no area is available so callers can fall back.
        const buildFloorDisplay = (areaM2, footprintM) => {
            if (typeof areaM2 !== 'number' || Number.isNaN(areaM2)) return null;
            const parts = [formatArea(areaM2, unit)];
            if (Array.isArray(footprintM) && footprintM.length >= 2) {
                const w = formatLinearMeasurement(footprintM[0], unit);
                const d = formatLinearMeasurement(footprintM[1], unit);
                parts.push(`Width ${w}${linearSuffix} · Depth ${d}${linearSuffix}`);
            }
            return parts.join(' · ');
        };

        // Prefer the per-section breakdown (one entry per scanned room). For a
        // single section, show just its area — no redundant Total. For 2+
        // sections, show each section's area (labeled) then a Total row.
        const validSections = Array.isArray(floorSections)
            ? floorSections.filter(s => typeof s?.floor_area === 'number' && !Number.isNaN(s.floor_area))
            : [];

        // Floor-dot color: theme token (matches the floor badge), not a hex
        // literal — theme-aware styling rule (CLAUDE.md).
        const floorColor = 'var(--tertiary-container-text)';

        let floorItems = [];
        if (validSections.length >= 2) {
            floorItems = validSections.map((s, idx) => ({
                id: s.section_uuid || `floor-section-${idx}`,
                type: 'floor',
                category: s.label || `Area ${idx + 1}`,
                displayStr: buildFloorDisplay(s.floor_area, s.floor_footprint_m),
                color: floorColor,
            }));
            // Total row — prefer the served top-level floor_area (m²); fall
            // back to summing the sections so we never render a blank total.
            // No footprint (the total spans multiple polygons).
            const totalAreaM2 = (typeof floorArea === 'number' && !Number.isNaN(floorArea))
                ? floorArea
                : validSections.reduce((sum, s) => sum + s.floor_area, 0);
            floorItems.push({
                id: 'floor-total',
                type: 'floor',
                category: 'Total floor area',
                displayStr: buildFloorDisplay(totalAreaM2, null),
                color: floorColor,
            });
        } else if (validSections.length === 1) {
            const s = validSections[0];
            floorItems = [{
                id: s.section_uuid || 'floor-0',
                type: 'floor',
                category: 'floor',
                displayStr: buildFloorDisplay(s.floor_area, s.floor_footprint_m),
                color: floorColor,
            }];
        } else {
            // No per-section data (legacy response or no computed area): fall
            // back to the top-level aggregate on the first floor surface, and
            // the raw dimensions triplet on the rest.
            const aggregate = buildFloorDisplay(floorArea, floorFootprintM);
            floorItems = floors.map((floor, idx) => ({
                id: floor.identifier,
                type: 'floor',
                category: 'floor',
                dimensions: floor.dimensions,
                displayStr: idx === 0 ? aggregate : null,
                color: floorColor,
            }));
        }
        if (floorItems.length > 0) {
            result.push({ type: 'floor', label: SECTION_LABELS.floor, items: floorItems });
        }

        const openingItems = openings.map(op => ({
            id: op.identifier,
            type: 'opening',
            category: 'opening',
            dimensions: op.dimensions,
            color: '#92400e',
        }));
        if (openingItems.length > 0) {
            result.push({ type: 'opening', label: SECTION_LABELS.opening, items: openingItems });
        }

        const doorItems = doors.map(door => ({
            id: door.identifier,
            type: 'door',
            category: 'door',
            dimensions: door.dimensions,
            color: '#78350f',
        }));
        if (doorItems.length > 0) {
            result.push({ type: 'door', label: SECTION_LABELS.door, items: doorItems });
        }

        const windowItems = windows.map(win => ({
            id: win.identifier,
            type: 'window',
            category: 'window',
            dimensions: win.dimensions,
            color: getCategoryColor('window'),
        }));
        if (windowItems.length > 0) {
            result.push({ type: 'window', label: SECTION_LABELS.window, items: windowItems });
        }

        const objectItems = objects.map(obj => ({
            id: obj.identifier,
            type: 'object',
            category: getCategoryName(obj.category),
            dimensions: obj.dimensions,
            color: getCategoryColor(obj.category),
        }));
        if (objectItems.length > 0) {
            result.push({ type: 'object', label: SECTION_LABELS.object, items: objectItems });
        }

        // Unknown: flatten the [{key, items}] grouping into per-item
        // rows so the list looks uniform. The parent key becomes the
        // category badge text ("ceilings", "roofs", whatever iOS sent).
        // Items have no schema — pull whatever identifier we can find
        // and fall back to the key + index so every row has a stable id.
        const unknownItems = [];
        for (const group of unknown) {
            if (!group || !Array.isArray(group.items)) continue;
            group.items.forEach((item, idx) => {
                const rawId = item && typeof item === 'object'
                    ? (item.identifier ?? item.id ?? null)
                    : null;
                unknownItems.push({
                    id: rawId ?? `unknown:${group.key}:${idx}`,
                    type: 'unknown',
                    category: group.key,
                    // No dimensions parsing — we don't know the shape.
                    // Pass undefined so the row renderer falls through
                    // to its empty-state branch.
                    dimensions: item?.dimensions,
                    // Amber dot. Matches the badge palette + the 3D
                    // scene's --unknown rendering so the visual story
                    // is consistent across surfaces.
                    color: '#f59e0b',
                });
            });
        }
        if (unknownItems.length > 0) {
            result.push({ type: 'unknown', label: SECTION_LABELS.unknown, items: unknownItems });
        }

        return result;
    }, [objects, walls, doors, windows, floors, openings, unknown,
        floorArea, floorFootprintM, floorSections, unitSystem]);

    // Flat lookup of every opening's derived measurement by its UUID, so a
    // door/window row can append its gap/corner/sill lines. Built from the
    // per-wall `wall_measurements` block (keyed by wall, each carrying an
    // `openings` array).
    const openingMeasureById = useMemo(() => {
        const map = {};
        for (const entry of Object.values(wallMeasurements || {})) {
            for (const op of entry?.openings || []) {
                if (op && op.uuid) map[op.uuid] = op;
            }
        }
        return map;
    }, [wallMeasurements]);

    // Flat view of every item across all sections, in render order. The
    // keyboard-nav handler and the selectedId scroll effect index into this
    // — arrow keys traverse the whole list, crossing section boundaries.
    const allItems = useMemo(
        () => sections.flatMap(section => section.items),
        [sections]
    );

    // Scroll selected item into view
    useEffect(() => {
        if (selectedId && listRef.current) {
            try {
                const escapedId = CSS.escape(selectedId);
                const selectedElement = listRef.current.querySelector(`[data-id="${escapedId}"]`);
                if (selectedElement && typeof selectedElement.scrollIntoView === 'function') {
                    selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            } catch (e) {
                console.warn('Failed to scroll to selected element:', e);
            }
        }
    }, [selectedId]);

    // Keyboard navigation handler
    const handleKeyDown = useCallback((e, itemId, index) => {
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                onSelect(itemId);
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (index < allItems.length - 1) {
                    const nextId = allItems[index + 1].id;
                    itemRefs.current[nextId]?.focus();
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (index > 0) {
                    const prevId = allItems[index - 1].id;
                    itemRefs.current[prevId]?.focus();
                }
                break;
            default:
                break;
        }
    }, [allItems, onSelect]);

    if (allItems.length === 0) {
        return (
            <div className={`p-4 text-center text-[var(--text-muted)] text-sm ${className}`}>
                No objects in scene
            </div>
        );
    }

    return (
        <div className={`bg-[var(--bg-card)] rounded-xl overflow-hidden flex flex-col shadow-[var(--shadow-sm)] ${className}`}>
            {/* Visually-hidden label so the listbox keeps an accessible name
                even though the visible UI is now per-section stat headers
                rather than one "Scene Objects" title. */}
            <h3 id="object-list-label" className="sr-only">Scene Objects</h3>
            <div
                ref={listRef}
                role="listbox"
                aria-labelledby="object-list-label"
                aria-activedescendant={selectedId ? `item-${selectedId}` : undefined}
                className="flex-1 overflow-y-auto"
            >
                {(() => {
                    // Running index across all sections so handleKeyDown's
                    // index argument keeps lining up with the flat `allItems`
                    // array — arrow-key nav crosses section boundaries.
                    let flatIndex = -1;
                    return sections.map(section => {
                        // Identifiers this section's eye toggle governs. Floor
                        // rows are area *summaries* (synthetic ids like
                        // `floor-total` / section uuids) that don't map 1:1 to
                        // scene meshes, so the floor header toggles the raw
                        // floor identifiers instead. Every other section's row
                        // id IS the scene identifier.
                        const sectionIds = section.type === 'floor'
                            ? floors.map(f => f.identifier).filter(Boolean)
                            : section.items.map(i => i.id);
                        const sectionVisibility = getSectionVisibility(sectionIds, hiddenIds);
                        const sectionHidden = sectionVisibility === 'none';
                        return (
                        <div key={section.type}>
                            {/* Section header doubles as the stat: label + count.
                                Styled after the old CAPTURED DATA stat rows.
                                role="presentation" keeps it out of the listbox's
                                option set. */}
                            <div
                                role="presentation"
                                className="sticky top-0 z-10 flex justify-between items-center px-4 py-2 bg-[var(--bg-hover)] border-b border-[var(--border)]"
                            >
                                <span className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                                    {section.label}
                                </span>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-mono font-bold text-[var(--text-main)]">
                                        {section.items.length}
                                    </span>
                                    {/* Section visibility toggle. Hides/shows every
                                        scene mesh in this group at once. Omitted when
                                        the section governs no real scene ids. */}
                                    {sectionIds.length > 0 && onToggleSection && (
                                        <button
                                            type="button"
                                            onClick={() => onToggleSection(sectionIds, sectionHidden)}
                                            aria-label={sectionHidden
                                                ? `Show all ${section.label}`
                                                : `Hide all ${section.label}`}
                                            aria-pressed={sectionHidden}
                                            title={sectionHidden
                                                ? `Show all ${section.label}`
                                                : `Hide all ${section.label}`}
                                            className="p-0.5 rounded text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-card)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                                        >
                                            {sectionHidden
                                                ? <EyeOff size={16} aria-hidden="true" />
                                                : <Eye size={16} aria-hidden="true" />}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {section.items.map(item => {
                                flatIndex += 1;
                                const index = flatIndex;
                                const isSelected = selectedId === item.id;
                                // Floor rows are area summaries, not scene
                                // meshes — they get no per-row eye (the floor
                                // header toggles the underlying meshes).
                                const isToggleable = item.type !== 'floor';
                                const isHidden = isToggleable && hiddenIds.has(item.id);
                                // Walls get the per-edge breakdown
                                // (Tall, Short, Slope, etc.) computed
                                // from their polygon outline. RoomPlan
                                // ships an explicit `polygonCorners` only
                                // for non-rectangular walls (gables,
                                // L-shapes); plain rectangular walls
                                // arrive with an empty array because the
                                // shape is implied by `dimensions` —
                                // synthesize the 4 corners in that case
                                // so every wall renders consistently.
                                let edgeStr = null;
                                if (item.type === 'wall') {
                                    const polygon = (item.polygonCorners && item.polygonCorners.length >= 3)
                                        ? item.polygonCorners
                                        : inferRectanglePolygon(item.dimensions);
                                    edgeStr = polygon ? formatWallEdges(polygon, unitSystem) : null;
                                }
                                // Unknown items have no schema — render
                                // a small notice instead of a fake
                                // "0.0 × 0.0 × 0.0 m" triplet that would
                                // imply we know the dimensions are zero.
                                let dimStr;
                                if (item.type === 'unknown') {
                                    dimStr = Array.isArray(item.dimensions) && item.dimensions.length > 0
                                        ? formatDimensionTriplet(item.dimensions, unitSystem)
                                        : 'Unrecognized scan item';
                                } else if (item.type === 'floor') {
                                    // Prefer the backend-computed area + polygon
                                    // footprint (ADR 0006). Fall back to the raw
                                    // dimensions triplet only when no area was
                                    // served AND a real dimensions array exists
                                    // (legacy scan); per-section rows carry no
                                    // `dimensions`, so guard against a fake
                                    // "0 × 0 × 0" triplet.
                                    dimStr = item.displayStr
                                        ?? (Array.isArray(item.dimensions) && item.dimensions.length > 0
                                            ? formatDimensionTriplet(item.dimensions, unitSystem)
                                            : null);
                                } else {
                                    dimStr = edgeStr ?? formatDimensionTriplet(item.dimensions, unitSystem);
                                }

                                // Append derived measurements as extra lines
                                // (each ' · ' segment renders on its own row).
                                // Walls get their net/paintable area; openings
                                // get the gap / nearest-corner / sill breakdown.
                                if (item.type === 'wall') {
                                    const netArea = wallMeasurements[item.id]?.net_area_m2;
                                    if (typeof netArea === 'number') {
                                        dimStr = `${dimStr} · Net ${formatArea(netArea, unitSystem)}`;
                                    }
                                } else if (item.type === 'door' || item.type === 'window' || item.type === 'opening') {
                                    const spacing = describeOpeningSpacing(openingMeasureById[item.id], unitSystem);
                                    if (spacing.length > 0) {
                                        dimStr = `${dimStr} · ${spacing.join(' · ')}`;
                                    }
                                }

                                return (
                                    <div
                                        key={item.id}
                                        id={`item-${item.id}`}
                                        ref={(el) => { itemRefs.current[item.id] = el; }}
                                        data-id={item.id}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        onClick={() => onSelect(item.id)}
                                        onKeyDown={(e) => handleKeyDown(e, item.id, index)}
                                        className={`flex items-center px-4 py-2 cursor-pointer transition-colors border-b border-[var(--border)] last:border-b-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--primary)]
                                            ${isSelected
                                                ? 'bg-[var(--error-container)] border-l-4 border-l-[var(--error)]'
                                                : 'hover:bg-[var(--bg-hover)] border-l-4 border-l-transparent'
                                            }
                                            ${isHidden ? 'opacity-50' : ''}`}
                                    >
                                        {/* var(--error) is inline because CSS variables can't be
                                            referenced via Tailwind's class-based color utilities. */}
                                        <span
                                            className="w-3 h-3 rounded-full mr-3 flex-shrink-0 shadow-sm"
                                            style={{ backgroundColor: isSelected ? 'var(--error)' : item.color }}
                                            aria-hidden="true"
                                        />

                                        {/* Object info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2">
                                                <span className={`text-sm font-medium capitalize ${isSelected ? 'text-[var(--error)]' : 'text-[var(--text-main)]'}`}>
                                                    {item.category}
                                                </span>
                                                <span className={`text-xs px-1.5 py-0.5 rounded ${getTypeBadgeClasses(item.type)}`}>
                                                    {item.type}
                                                </span>
                                            </div>
                                            <div className="text-xs text-[var(--text-muted)] break-words mt-0.5">
                                                {/* `dimStr` is always assigned a string above, but guard
                                                    with `|| ''` so a future refactor that yields null/undefined
                                                    can't crash .split() (renders a harmless empty line). */}
                                                {(dimStr || '').split(' · ').map((part, partIdx) => (
                                                    <div key={partIdx}>{part}</div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Selection indicator */}
                                        {isSelected && (
                                            <svg className="w-5 h-5 text-[var(--error)] flex-shrink-0 mr-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}

                                        {/* Per-row visibility toggle. stopPropagation
                                            so toggling visibility doesn't also select
                                            the row (mouse + keyboard). Floor summary
                                            rows have no mesh to toggle, so no eye. */}
                                        {isToggleable && onToggleItem && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onToggleItem(item.id); }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                                                }}
                                                aria-label={isHidden
                                                    ? `Show ${item.category}`
                                                    : `Hide ${item.category}`}
                                                aria-pressed={isHidden}
                                                title={isHidden
                                                    ? `Show ${item.category}`
                                                    : `Hide ${item.category}`}
                                                className="p-1 rounded flex-shrink-0 text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-card)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                                            >
                                                {isHidden
                                                    ? <EyeOff size={16} aria-hidden="true" />
                                                    : <Eye size={16} aria-hidden="true" />}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        );
                    });
                })()}
            </div>
        </div>
    );
};

// PropTypes for validation
const sceneObjectShape = PropTypes.shape({
    identifier: PropTypes.string.isRequired,
    category: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    dimensions: PropTypes.arrayOf(PropTypes.number),
    // Walls carry an optional polygon outline. Casing normalization to
    // this canonical camelCase field happens upstream in
    // FloorPlanViewer.jsx::normalizePolygonCasing. When present and
    // the type is 'wall', the list row renders per-edge measurements
    // instead of the bounding-box triplet.
    polygonCorners: PropTypes.array,
});

SceneObjectList.propTypes = {
    /** Array of scene objects with identifier, category, dimensions */
    objects: PropTypes.arrayOf(sceneObjectShape),
    /** Array of wall objects */
    walls: PropTypes.arrayOf(sceneObjectShape),
    /** Array of door objects */
    doors: PropTypes.arrayOf(sceneObjectShape),
    /** Array of window objects */
    windows: PropTypes.arrayOf(sceneObjectShape),
    /** Array of floor objects */
    floors: PropTypes.arrayOf(sceneObjectShape),
    /** Backend-computed floor area in m² (display only; ADR 0006) */
    floorArea: PropTypes.number,
    /** Floor area provenance: 'polygon_computed' | 'extracted' | 'user_override' */
    floorAreaSource: PropTypes.string,
    /** True polygon footprint [width, depth] in meters */
    floorFootprintM: PropTypes.arrayOf(PropTypes.number),
    /** Per-section floor breakdown: [{section_uuid, label, floor_area (m²), floor_area_source, floor_footprint_m}] */
    floorSections: PropTypes.arrayOf(PropTypes.shape({
        section_uuid: PropTypes.string,
        label: PropTypes.string,
        floor_area: PropTypes.number,
        floor_area_source: PropTypes.string,
        floor_footprint_m: PropTypes.arrayOf(PropTypes.number),
    })),
    /** Array of opening (doorless passage) objects */
    openings: PropTypes.arrayOf(sceneObjectShape),
    /**
     * Diagnostic bucket: items the backend ingest didn't recognize.
     * Shape: [{key: 'ceilings', items: [...raw items...]}]
     */
    unknown: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        items: PropTypes.array.isRequired,
    })),
    /**
     * Derived wall measurements keyed by wall UUID:
     * { [wallUuid]: { net_area_m2, openings: [{uuid, gap_to_prev_m,
     *   edge_to_start_m, edge_to_end_m, sill_m, ...}] } }. Display only —
     * computed server-side (services/wall_geometry.py).
     */
    wallMeasurements: PropTypes.object,
    /** Currently selected object ID, or null if none */
    selectedId: PropTypes.string,
    /** Callback when item is selected, receives object ID */
    onSelect: PropTypes.func.isRequired,
    /** Set of scene identifiers currently hidden from the 3D view */
    hiddenIds: PropTypes.instanceOf(Set),
    /** Toggle one object's visibility, receives object ID */
    onToggleItem: PropTypes.func,
    /** Toggle a group's visibility, receives (ids[], makeVisible) */
    onToggleSection: PropTypes.func,
    /** Unit system for displayed dimensions */
    unitSystem: PropTypes.oneOf(Object.values(UNIT_SYSTEMS)),
    /** Additional CSS classes */
    className: PropTypes.string,
};

export default SceneObjectList;
